/**
 * BRAND POLICY: source_url contains the retailer URL (often with ASIN/SKU
 * as path segment). Use it ONLY as an <a href={source_url}> target. Never
 * print it as on-page text, in tooltips, or in schema markup. Public surface
 * is manufacturer model + FIN only — see plan section "Brand-policy guard
 * (locked May 8, 2026)".
 */

/**
 * CustomerReviews — Phase 8 deliverable, upgraded for the PIP lean rebuild
 * (WS2 full-text harvest: 80k+ AJ Madison reviews with bodies + customer
 * photos, plus the fin_review_rollup cross-source aggregate).
 *
 * Data flow: page.tsx fetches a capped top page of fin_reviews_raw + a
 * ratings histogram sample + fin_review_rollup, runs getDisplayReviews
 * (moderation gating, sort), and passes `reviews` + `meta` here. Zero live
 * retailer calls at render time (Rule 4).
 *
 * Behavior:
 *   - Real-or-Hidden (Rule 3): fewer than 3 reviews → null (no placeholder).
 *   - NEW-only (Rule 1): caller pre-filters condition_excluded=false.
 *   - Summary header: average + histogram + "Across the web" rollup chips.
 *   - Per retailer: top reviews (helpful → photos → recent, resolver order),
 *     "Show more" reveals more of the fetched page in place; long bodies
 *     expand inline (we host full text now — no forced link-outs).
 *   - Customer photos render as a thumbnail strip with a click-to-zoom
 *     lightbox.
 *   - meta.pendingModeration (dev preview only) badges the module so
 *     pre-moderation content is never mistaken for production-ready.
 */

'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reviewSourceLabel } from '@/lib/render-policy/display-reviews';
import { SignedOfferLink } from '@/components/product/signed-offer-link';

export interface CustomerReviewRow {
  id: string;
  source: string;
  source_url: string | null;
  rating: number | null; // 1-5
  title: string | null;
  body: string | null;
  author_name: string | null;
  author_verified: boolean | null;
  posted_at: string | null; // ISO-8601
  helpful_count: number | null;
  review_imgs?: string[] | null;
  // condition_excluded NOT included — caller pre-filters
}

export interface ReviewsMeta {
  avgRating: number | null;
  totalRatings: number;
  /** Index 0 = 5★ … index 4 = 1★. */
  histogram: [number, number, number, number, number];
  rollup: {
    sources: Array<{ source: string; label: string; rating: number | null; count: number }>;
    weightedRating: number | null;
    totalReviews: number;
  } | null;
  pendingModeration: boolean;
}

interface CustomerReviewsProps {
  reviews: CustomerReviewRow[] | null | undefined;
  meta?: ReviewsMeta | null;
  /** FRIDGE-179/crawler-eye 08/22: the page flight ships only the initial
   * review window. Called (fire-and-forget) on the FIRST interaction that
   * needs the full corpus — search, star filter, photos-only, sort,
   * show-more — so the parent can fetch /api/reviews/[fin] once and swap the
   * `reviews` prop. Filtering below recomputes when it lands. */
  onNeedCorpus?: () => void;
  /** True while the parent's corpus fetch is in flight — renders an honest
   * "loading all reviews" note beside the controls instead of silently
   * filtering a partial set. */
  corpusLoading?: boolean;
  /** Size of the full eligible corpus (summary.eligibleCount). Used only for
   * the loading note; counts shown elsewhere come from the histogram. */
  corpusTotal?: number;
  /** P2: buybox-winner CTA rendered after the first block of reviews — the
   * "convinced" moment — and pinned inside the photo lightbox. Always renders
   * through SignedOfferLink (HMAC-signed click, item 17; client-minted cid,
   * FRIDGE-276): the caller passes the offer, never a pre-built href, so no
   * unsigned/cid-less anchor can be rendered here. */
  inlineCta?: { retailer: string; priceDisplay: string; offerId: string; productId?: string } | null;
  className?: string;
}

// Stable display order for known retailers; anything new lands after these.
const RETAILER_ORDER: ReadonlyArray<string> = [
  'amazon',
  'home_depot',
  'best_buy',
  'lowes',
  'walmart',
  'aj_madison',
];

const REVIEWS_PER_RETAILER = 5;
const SHOW_MORE_STEP = 10;
const BODY_TRUNCATE_CHARS = 280;
const PHOTO_STRIP_MAX = 4;

export function formatPostedAt(iso: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  try {
    return new Date(t).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return '';
  }
}

function clampRating(rating: number | null): number {
  if (rating === null || Number.isNaN(rating)) return 0;
  if (rating < 0) return 0;
  if (rating > 5) return 5;
  return rating;
}

function StarRow({ rating, sizeClass = 'text-sm' }: { rating: number | null; sizeClass?: string }) {
  const r = clampRating(rating);
  const filled = Math.round(r);
  const empty = 5 - filled;
  const label = `${r.toFixed(1)} out of 5 stars`;
  return (
    <span
      className={`inline-flex items-center text-[#ff9951] ${sizeClass} leading-none`}
      aria-label={label}
      role="img"
    >
      <span aria-hidden="true">{'★'.repeat(filled)}{'☆'.repeat(empty)}</span>
    </span>
  );
}

function VerifiedBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium"
      data-testid="verified-buyer-badge"
    >
      <svg aria-hidden="true" className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      Verified
    </span>
  );
}

interface RetailerGroup {
  source: string;
  label: string;
  reviews: CustomerReviewRow[];
  avgRating: number;
}

function buildRetailerGroups(reviews: CustomerReviewRow[]): RetailerGroup[] {
  const byRetailer = new Map<string, CustomerReviewRow[]>();
  for (const r of reviews) {
    if (!r?.source) continue;
    const arr = byRetailer.get(r.source);
    if (arr) arr.push(r);
    else byRetailer.set(r.source, [r]);
  }

  // Known retailers in fixed order, then any new sources by review count.
  const orderedSources = [
    ...RETAILER_ORDER.filter((s) => byRetailer.has(s)),
    ...[...byRetailer.keys()]
      .filter((s) => !RETAILER_ORDER.includes(s))
      .sort((a, b) => (byRetailer.get(b)?.length ?? 0) - (byRetailer.get(a)?.length ?? 0)),
  ];

  const groups: RetailerGroup[] = [];
  for (const source of orderedSources) {
    const arr = byRetailer.get(source);
    if (!arr || arr.length === 0) continue;
    // Rows arrive resolver-sorted (helpful → photos → recent); keep that order.
    const ratingsWithValues = arr
      .map((r) => r.rating)
      .filter((v): v is number => v !== null && !Number.isNaN(v));
    const avgRating =
      ratingsWithValues.length > 0
        ? ratingsWithValues.reduce((s, v) => s + v, 0) / ratingsWithValues.length
        : 0;
    groups.push({ source, label: reviewSourceLabel(source), reviews: arr, avgRating });
  }
  return groups;
}

function HistogramBars({
  histogram,
  total,
  activeStar,
  onSelectStar,
}: {
  histogram: ReviewsMeta['histogram'];
  total: number;
  activeStar?: number | null;
  onSelectStar?: (star: number | null) => void;
}) {
  if (total <= 0) return null;
  return (
    <div className="space-y-1" data-testid="reviews-histogram">
      {histogram.map((count, i) => {
        const star = 5 - i;
        const pct = Math.round((count / total) * 100);
        const active = activeStar === star;
        const row = (
          <>
            <span className={`w-7 text-right shrink-0 ${active ? 'font-bold text-gray-900' : ''}`}><span className="text-black">{star}</span>★</span>
            <div className="flex-1 h-2 rounded-full bg-white overflow-hidden">
              <div
                className={`h-full rounded-full ${active ? 'bg-[#ff8533]' : 'bg-[#ff9951]'}`}
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
            </div>
            <span className="w-10 shrink-0 tabular-nums text-black">{pct}%</span>
          </>
        );
        // Owner QA 07-06 (interactivity): histogram rows double as star filters.
        return onSelectStar ? (
          <button
            key={star}
            type="button"
            onClick={() => onSelectStar(active ? null : star)}
            aria-pressed={active}
            className={`w-full flex items-center gap-2 text-xs text-gray-600 rounded px-1 -mx-1 transition-colors hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40 ${active ? 'bg-white' : ''}`}
            title={active ? 'Clear star filter' : `Show only ${star}-star reviews`}
          >
            {row}
          </button>
        ) : (
          <div key={star} className="flex items-center gap-2 text-xs text-gray-600">{row}</div>
        );
      })}
    </div>
  );
}

function PhotoStrip({
  imgs,
  onZoom,
}: {
  imgs: string[];
  onZoom: (index: number) => void;
}) {
  const visible = imgs.slice(0, PHOTO_STRIP_MAX);
  const extra = imgs.length - visible.length;
  return (
    <div className="mt-2 flex items-center gap-2" data-testid="review-photos">
      {visible.map((src, i) => (
        <button
          key={`${src}-${i}`}
          type="button"
          onClick={() => onZoom(i)}
          className="relative w-14 h-14 rounded border border-gray-200 overflow-hidden hover:ring-2 hover:ring-[#0469f0] focus:outline-none focus:ring-2 focus:ring-[#0469f0]"
          aria-label={`View customer photo ${i + 1} of ${imgs.length}`}
        >
          {/* Customer photos live on retailer CDNs — plain <img>, lazy. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={src} alt={`Customer photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
          {extra > 0 && i === visible.length - 1 && (
            <span className="absolute inset-0 bg-black/50 text-white text-xs font-semibold flex items-center justify-center">
              +{extra}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

type ReviewSortMode = 'top' | 'helpful' | 'newest' | 'highest' | 'lowest';

export function CustomerReviews({ reviews, meta, inlineCta, className = '', onNeedCorpus, corpusLoading = false, corpusTotal }: CustomerReviewsProps) {
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});
  const [expandedBodies, setExpandedBodies] = useState<Record<string, boolean>>({});
  // Photo lightbox as a navigable GALLERY (owner QA 07/20): store the whole
  // image set for the clicked review + the active index, so Esc/←/→ and swipe
  // move through the photos. Replaces the single-src modal that froze the page.
  const [lightbox, setLightbox] = useState<{ imgs: string[]; index: number } | null>(null);
  const touchStartX = useRef<number | null>(null);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const stepLightbox = useCallback((delta: number) => {
    setLightbox((lb) => {
      if (!lb || lb.imgs.length < 2) return lb;
      const n = lb.imgs.length;
      return { ...lb, index: (lb.index + delta + n) % n };
    });
  }, []);

  // Owner QA 07-06: full interactivity — search, star filter, photos-only,
  // sort. Any active control switches from the grouped-by-retailer layout to
  // a flat result list (cross-retailer sorts don't make sense grouped).
  const [query, setQuery] = useState('');
  const [starFilter, setStarFilter] = useState<number | null>(null);
  const [photosOnly, setPhotosOnly] = useState(false);
  const [sortMode, setSortMode] = useState<ReviewSortMode>('top');
  const [flatVisible, setFlatVisible] = useState(10);

  const filtersActive =
    query.trim().length > 0 || starFilter !== null || photosOnly || sortMode !== 'top';

  // While the corpus fetch is in flight AND the user is interacting, say so —
  // filtering a partial window silently would be a quiet lie about coverage.
  const corpusNote =
    corpusLoading && (filtersActive || (reviews?.length ?? 0) < (corpusTotal ?? 0))
      ? `Loading all ${corpusTotal ?? ''} reviews…`
      : null;

  const filtered = useMemo(() => {
    let list = reviews ?? [];
    if (starFilter !== null) {
      list = list.filter((r) => Math.round(clampRating(r.rating)) === starFilter && r.rating !== null);
    }
    if (photosOnly) list = list.filter((r) => (r.review_imgs?.length ?? 0) > 0);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) => (r.body ?? '').toLowerCase().includes(q) || (r.title ?? '').toLowerCase().includes(q)
      );
    }
    switch (sortMode) {
      case 'helpful':
        list = [...list].sort((a, b) => (b.helpful_count ?? 0) - (a.helpful_count ?? 0));
        break;
      case 'newest':
        list = [...list].sort(
          (a, b) => (Date.parse(b.posted_at ?? '') || 0) - (Date.parse(a.posted_at ?? '') || 0)
        );
        break;
      case 'highest':
        list = [...list].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
        break;
      case 'lowest':
        list = [...list].sort((a, b) => (a.rating ?? 6) - (b.rating ?? 6));
        break;
      default:
        break; // 'top' keeps resolver order (positive → helpful → photos → recent)
    }
    return list;
  }, [reviews, query, starFilter, photosOnly, sortMode]);

  // Keyboard: Escape closes, ←/→ move through the gallery. We deliberately do
  // NOT lock body scroll (owner rule: the page must never be permanently
  // frozen) — the overlay itself is a scroll container, and the offer strip
  // keeps the affiliate clickout reachable while it is open.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') stepLightbox(1);
      else if (e.key === 'ArrowLeft') stepLightbox(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, closeLightbox, stepLightbox]);

  if (!reviews || reviews.length < 3) return null;

  // Shared review card — used by both the grouped and flat layouts.
  const renderReview = (r: CustomerReviewRow, showSource: boolean) => {
    const body = r.body ?? '';
    const needsTruncate = body.length > BODY_TRUNCATE_CHARS;
    const expanded = !!expandedBodies[r.id];
    const shownBody =
      !needsTruncate || expanded
        ? body
        : (() => {
            const slice = body.slice(0, BODY_TRUNCATE_CHARS);
            const lastSpace = slice.lastIndexOf(' ');
            return (lastSpace > BODY_TRUNCATE_CHARS * 0.6 ? slice.slice(0, lastSpace) : slice).trimEnd() + '…';
          })();
    const dateLabel = formatPostedAt(r.posted_at);
    const imgs = (r.review_imgs ?? []).filter((u) => typeof u === 'string' && u.length > 0);
    return (
      <li key={r.id} className="rounded-lg bg-white p-4 shadow-sm shadow-black/[0.03]">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
          <StarRow rating={r.rating} />
          {r.title && (
            <span className="text-sm font-bold text-gray-900">{r.title}</span>
          )}
          {showSource && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
              {reviewSourceLabel(r.source)}
            </span>
          )}
        </div>
        {body && (
          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
            {shownBody}
            {needsTruncate && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => setExpandedBodies((prev) => ({ ...prev, [r.id]: !expanded }))}
                  className="text-blue-700 hover:underline whitespace-nowrap text-sm font-medium"
                >
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              </>
            )}
          </p>
        )}
        {imgs.length > 0 && <PhotoStrip imgs={imgs} onZoom={(index) => setLightbox({ imgs, index })} />}
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
          {r.author_name && (
            <span className="font-semibold text-gray-600">{r.author_name}</span>
          )}
          {r.author_verified === true && <VerifiedBadge />}
          {dateLabel && <span>{dateLabel}</span>}
          {typeof r.helpful_count === 'number' && r.helpful_count > 0 && (
            <span>{r.helpful_count} found this helpful</span>
          )}
        </div>
      </li>
    );
  };

  const groups = buildRetailerGroups(reviews);
  const totalRendered = groups.reduce((s, g) => s + g.reviews.length, 0);
  if (totalRendered < 3 || groups.length === 0) return null;

  const retailerCount = groups.length;
  const summaryTotal = meta?.totalRatings ?? 0;
  const showSummary = !!meta && summaryTotal > 0 && meta.avgRating !== null;
  // The generic `aggregated_reviews` rollup source is data plumbing, not a
  // shopper-meaningful name — as a chip it only repeats the header average
  // (owner QA 07-06: "Aggregated Reviews 4.6★ (300)" read as noise). Named
  // sources (Amazon, AJ Madison, …) remain.
  const rollupSources =
    meta?.rollup?.sources?.filter((s) => s.count > 0 && s.source !== 'aggregated_reviews') ?? [];

  return (
    <section
      className={`rounded-xl bg-gray-50 p-4 sm:p-6 my-4 ${className}`}
      aria-label="Customer reviews"
      data-testid="customer-reviews"
      data-retailer-count={retailerCount}
      data-review-count={totalRendered}
    >
      <header className="mb-5">
        <p className="text-[#ff9951] font-bold tracking-widest text-[11px] uppercase mb-1.5">
          What Owners Say
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl md:text-2xl font-black tracking-tighter text-gray-900">
            Customer Reviews
          </h2>
          {meta?.pendingModeration && (
            <span
              className="inline-flex items-center text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700"
              data-testid="reviews-pending-moderation"
              title="Dev preview: moderation pass has not run — not production-eligible yet"
            >
              Pre-moderation preview
            </span>
          )}
        </div>
      </header>

      {/* Summary: average + histogram + cross-source rollup */}
      {showSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] lg:grid-cols-[auto_1fr_auto] gap-x-8 gap-y-4 mb-4 rounded-lg bg-[#0469f0]/[0.04] p-4 sm:p-5 items-center" data-testid="reviews-summary">
          <div className="flex flex-col items-start">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-black tracking-tighter text-black">{meta!.avgRating!.toFixed(1)}</span>
              <span className="text-sm text-black">/ 5</span>
            </div>
            <StarRow rating={meta!.avgRating} sizeClass="text-base" />
            <p className="text-xs text-black mt-1">
              {summaryTotal.toLocaleString()} rating{summaryTotal === 1 ? '' : 's'}
            </p>
          </div>
          <div className="max-w-sm w-full">
            <HistogramBars
              histogram={meta!.histogram}
              total={summaryTotal}
              activeStar={starFilter}
              onSelectStar={setStarFilter}
            />
          </div>
          {rollupSources.length > 0 && (
            <div className="lg:max-w-xs" data-testid="reviews-rollup">
              <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-2">
                What the internet says
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {rollupSources.map((s) => (
                  <li
                    key={s.source}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white shadow-sm text-gray-700"
                  >
                    <span className="font-medium">{s.label}</span>
                    {s.rating !== null && (
                      <span className="text-black font-semibold">{s.rating.toFixed(1)}<span className="text-[#ff8533]">★</span></span>
                    )}
                    <span className="text-black">({s.count.toLocaleString()})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Toolbar — search / sort / star chips / photos toggle (owner QA 07-06). */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="reviews-toolbar">
        <input
          type="search"
          value={query}
          onChange={(e) => { onNeedCorpus?.(); setQuery(e.target.value); setFlatVisible(10); }}
          placeholder="Search reviews…"
          aria-label="Search reviews"
          className="flex-1 min-w-[160px] max-w-xs text-sm px-3 py-1.5 rounded-lg bg-white shadow-sm text-gray-900 placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40"
          data-testid="reviews-search"
        />
        <select
          value={sortMode}
          onChange={(e) => { onNeedCorpus?.(); setSortMode(e.target.value as ReviewSortMode); setFlatVisible(10); }}
          aria-label="Sort reviews"
          className="text-sm px-2.5 py-1.5 rounded-lg bg-white shadow-sm text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40"
          data-testid="reviews-sort"
        >
          <option value="top">Top reviews</option>
          <option value="helpful">Most helpful</option>
          <option value="newest">Newest</option>
          <option value="highest">Highest rating</option>
          <option value="lowest">Lowest rating</option>
        </select>
        <div className="flex items-center gap-1" role="group" aria-label="Filter by star rating">
          {[5, 4, 3, 2, 1].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { onNeedCorpus?.(); setStarFilter(starFilter === s ? null : s); setFlatVisible(10); }}
              aria-pressed={starFilter === s}
              className={`text-xs px-2.5 py-1 rounded-full shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40 ${
                starFilter === s
                  ? 'bg-[#0469f0] text-white font-semibold'
                  : 'bg-white text-gray-600 hover:text-gray-900'
              }`}
            >
              {s}★
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => { onNeedCorpus?.(); setPhotosOnly(!photosOnly); setFlatVisible(10); }}
          aria-pressed={photosOnly}
          className={`text-xs px-2.5 py-1 rounded-full shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40 ${
            photosOnly
              ? 'bg-[#0469f0] text-white font-semibold'
              : 'bg-white text-gray-600 hover:text-gray-900'
          }`}
          data-testid="reviews-photos-toggle"
        >
          📷 With photos
        </button>
        {filtersActive && (
          <button
            type="button"
            onClick={() => { setQuery(''); setStarFilter(null); setPhotosOnly(false); setSortMode('top'); setFlatVisible(10); }}
            className="text-xs font-medium text-blue-700 hover:underline"
            data-testid="reviews-clear-filters"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Flat result view when any control is active (cross-retailer sorts
          don't make sense inside per-retailer groups). */}
      {filtersActive && (
        <div data-testid="reviews-flat-results">
          <p className="text-xs text-gray-500 mb-2">
            {filtered.length} review{filtered.length === 1 ? '' : 's'} match
            {summaryTotal > (reviews?.length ?? 0) && (
              <> · searching within the top {reviews!.length} of {summaryTotal.toLocaleString()} reviews</>
            )}
          </p>
          {corpusNote && (
            <p className="text-xs text-gray-500 mb-2" data-testid="reviews-corpus-loading">{corpusNote}</p>
          )}
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-600 py-6 text-center">
              {corpusLoading ? 'Loading all reviews…' : 'No reviews match — try clearing a filter.'}
            </p>
          ) : (
            <>
              <ul className="space-y-3">
                {filtered.slice(0, flatVisible).map((r) => renderReview(r, true))}
              </ul>
              {filtered.length > flatVisible && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => { onNeedCorpus?.(); setFlatVisible(flatVisible + SHOW_MORE_STEP); }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
                    data-testid="reviews-show-more"
                  >
                    Show {Math.min(SHOW_MORE_STEP, filtered.length - flatVisible)} more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!filtersActive && (
      <div className="space-y-6">
        {groups.map((g, gIndex) => {
          const visibleCount = visibleCounts[g.source] ?? REVIEWS_PER_RETAILER;
          const visible = g.reviews.slice(0, visibleCount);
          const moreCount = g.reviews.length - visible.length;
          return (
            <div key={`group-${g.source}`} data-retailer={g.source}>
              <h3 className="text-sm font-bold text-gray-900 mb-2.5 flex items-baseline gap-2">
                <span>{g.label}</span>
                <span className="text-xs font-normal text-gray-500">
                  {/* Per-source totals come from the whole-FIN rollup when it
                      carries this source — the loaded window is a SUBSET
                      (SERIALIZED_REVIEWS_MAX pre-fetch), so counting loaded
                      rows here would under-report the retailer. Fallback to
                      the loaded count only when the rollup lacks the source. */}
                  {(() => {
                    const rolled = rollupSources.find((s) => s.source === g.source)
                    const n = rolled?.count ?? g.reviews.length
                    const avg = rolled?.rating ?? g.avgRating
                    return <>{n} review{n === 1 ? '' : 's'} · {typeof avg === 'number' ? avg.toFixed(1) : g.avgRating.toFixed(1)}★ avg</>
                  })()}
                </span>
              </h3>

              <ul className="space-y-3">
                {visible.map((r) => renderReview(r, false))}
              </ul>

              {/* Retailer review-page link-out removed (conversion spec P0):
                  a non-monetized exit. We host the full corpus — "Show more"
                  reveals it in place. */}
              {moreCount > 0 && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      onNeedCorpus?.();
                      setVisibleCounts((prev) => ({
                        ...prev,
                        [g.source]: visibleCount + SHOW_MORE_STEP,
                      }));
                    }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
                    data-testid="reviews-show-more"
                  >
                    Show {Math.min(SHOW_MORE_STEP, moreCount)} more review{Math.min(SHOW_MORE_STEP, moreCount) === 1 ? '' : 's'}
                  </button>
                </div>
              )}

              {/* P2: post-reading conversion moment — after the first block
                  of reviews, hand high intent straight to the best offer. */}
              {gIndex === 0 && inlineCta && visible.length >= 3 && (
                <div
                  className="mt-4 rounded-lg bg-[#ff9951]/[0.06] px-4 py-3.5 flex flex-wrap items-center justify-between gap-3"
                  data-testid="reviews-inline-cta"
                >
                  <p className="text-sm text-gray-800">
                    Convinced? <span className="font-semibold">{inlineCta.retailer}</span> has it for{' '}
                    <span className="font-black">{inlineCta.priceDisplay}</span>
                  </p>
                  <SignedOfferLink
                    offerId={inlineCta.offerId}
                    productId={inlineCta.productId}
                    pos="reviews-inline"
                    target="_blank"
                    className="bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] px-5 py-2 rounded-lg font-bold text-xs transition-all shadow-sm active:scale-95"
                  >
                    View Deal
                  </SignedOfferLink>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Customer-photo lightbox — a navigable, SCROLLABLE gallery (owner QA
          07/20). The overlay is itself a vertical scroll container (never a
          hard body-scroll lock), so short viewports can always reach the image
          and controls. Esc closes; ←/→ and on-screen chevrons and mobile swipe
          move through the review's photos. A compact offer strip pins the top
          deal + "View Deal" to the bottom so the affiliate clickout is never
          blocked while a photo is open. */}
      {lightbox && (() => {
        const total = lightbox.imgs.length;
        const src = lightbox.imgs[lightbox.index];
        const hasMany = total > 1;
        return (
        <div
          className="fixed inset-0 z-50 bg-black/85 overflow-y-auto overscroll-contain flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={`Customer photo ${lightbox.index + 1} of ${total}`}
          onClick={closeLightbox}
          data-testid="review-photo-lightbox"
        >
          {/* Close — always reachable at top-right. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="fixed top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 text-gray-900 text-xl font-bold flex items-center justify-center shadow hover:bg-white focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Close photo"
            data-testid="review-photo-close"
          >
            ×
          </button>

          {/* Stage — grows to fill, but the parent scrolls if it can't. Swipe
              left/right on touch to move through the gallery. */}
          <div
            className="flex-1 flex items-center justify-center gap-2 sm:gap-4 px-3 py-14 min-h-[60vh]"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { touchStartX.current = e.touches[0]?.clientX ?? null; }}
            onTouchEnd={(e) => {
              const start = touchStartX.current;
              touchStartX.current = null;
              if (start === null || !hasMany) return;
              const dx = (e.changedTouches[0]?.clientX ?? start) - start;
              if (Math.abs(dx) > 40) stepLightbox(dx < 0 ? 1 : -1);
            }}
          >
            {hasMany && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
                className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 text-gray-900 text-2xl font-bold flex items-center justify-center shadow hover:bg-white focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Previous photo"
                data-testid="review-photo-prev"
              >
                ‹
              </button>
            )}
            <div className="flex flex-col items-center gap-3 min-w-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={src}
                alt={`Customer photo ${lightbox.index + 1} of ${total} (zoomed)`}
                className="max-w-full max-h-[75vh] rounded shadow-2xl object-contain"
              />
              {hasMany && (
                <span
                  className="text-xs font-medium text-white/90 bg-black/40 rounded-full px-3 py-1 tabular-nums"
                  data-testid="review-photo-counter"
                >
                  {lightbox.index + 1} / {total}
                </span>
              )}
            </div>
            {hasMany && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
                className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 text-gray-900 text-2xl font-bold flex items-center justify-center shadow hover:bg-white focus:outline-none focus:ring-2 focus:ring-white"
                aria-label="Next photo"
                data-testid="review-photo-next"
              >
                ›
              </button>
            )}
          </div>

          {/* Offer clickout strip — keeps the buy path reachable while the
              lightbox is open (owner rule: the affiliate clickout is never
              blocked). Pinned to the bottom of the viewport. */}
          {inlineCta && (
            <div
              className="sticky bottom-0 inset-x-0 bg-white/95 backdrop-blur px-4 py-3 flex flex-wrap items-center justify-between gap-3 shadow-[0_-2px_12px_rgba(0,0,0,0.15)]"
              onClick={(e) => e.stopPropagation()}
              data-testid="review-photo-offer-strip"
            >
              <p className="text-sm text-gray-800 min-w-0">
                <span className="font-semibold">{inlineCta.retailer}</span>{' '}
                <span className="font-black">{inlineCta.priceDisplay}</span>
              </p>
              <SignedOfferLink
                offerId={inlineCta.offerId}
                productId={inlineCta.productId}
                pos="reviews-lightbox"
                target="_blank"
                className="bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] px-5 py-2 rounded-lg font-bold text-xs transition-all shadow-sm active:scale-95 shrink-0"
              >
                View Deal
              </SignedOfferLink>
            </div>
          )}
        </div>
        );
      })()}
    </section>
  );
}

export default CustomerReviews;
